import createOpenapiClient, { type Client as OpenapiClient } from "openapi-fetch";
import { WardkeyAPIError, toAPIError } from "./errors";
import type { components, paths } from "./generated/types";

export const DEFAULT_API_BASE_URL = "https://api.wardkey.app";
const REQUEST_ID_HEADER = "x-request-id";

export type WardkeyClientOptions = {
  /** Base URL of the Wardkey API. Defaults to `https://api.wardkey.app`. */
  baseUrl?: string;
  /**
   * Bearer API token. When set, every request gets `Authorization: Bearer <token>`.
   * Mutually exclusive with `useCookies`.
   */
  apiToken?: string;
  /**
   * Send cross-origin cookies. Used when the SDK runs in the browser inside the
   * console — the same-origin BFF mints `x-wardkey-user` from the session cookie
   * before forwarding to the API. Mutually exclusive with `apiToken`.
   */
  useCookies?: boolean;
  /** Custom fetch — exists so tests can inject a mock. */
  fetch?: typeof fetch;
  /** Extra headers attached to every request. */
  headers?: Record<string, string>;
};

type Schemas = components["schemas"];

export type Identity = Schemas["Identity"];
export type UserRecord = Schemas["UserRecord"];
export type UpdateMeBody = Schemas["UpdateMeBody"];
export type OrgRecord = Schemas["OrgRecord"];
export type CreateOrgBody = Schemas["CreateOrgBody"];
export type UpdateOrgBody = Schemas["UpdateOrgBody"];
export type MembersList = Schemas["MembersList"];
export type MemberListEntry = Schemas["MemberListEntry"];
export type InviteMemberBody = Schemas["InviteMemberBody"];
export type InviteMemberResponse = Schemas["InviteMemberResponse"];
export type ChangeRoleBody = Schemas["ChangeRoleBody"];
export type MembershipRecord = Schemas["MembershipRecord"];
export type MemberRole = Schemas["MemberRole"];
export type OrgPlan = Schemas["OrgPlan"];
export type KekProvider = Schemas["KekProvider"];
export type SecretsList = Schemas["SecretsList"];
export type SecretListItem = Schemas["SecretListItem"];
export type SecretMeta = Schemas["SecretMeta"];
export type SecretValue = Schemas["SecretValue"];
export type CreateSecretBody = Schemas["CreateSecretBody"];
export type UpdateSecretBody = Schemas["UpdateSecretBody"];
export type SecretVersionResponse = Schemas["SecretVersionResponse"];
export type SetSecretVisibilityBody = Schemas["SetSecretVisibilityBody"];
export type Visibility = Schemas["Visibility"];
export type VersionsList = Schemas["VersionsList"];
export type VersionMeta = Schemas["VersionMeta"];
export type ServiceTokenRecord = Schemas["ServiceTokenRecord"];
export type ServiceTokensList = Schemas["ServiceTokensList"];
export type MintServiceTokenBody = Schemas["MintServiceTokenBody"];
export type MintServiceTokenResponse = Schemas["MintServiceTokenResponse"];
export type ProjectRecord = Schemas["ProjectRecord"];
export type CreateProjectBody = Schemas["CreateProjectBody"];
export type UpdateProjectBody = Schemas["UpdateProjectBody"];
export type ProjectsList = Schemas["ProjectsList"];
export type EnvironmentRecord = Schemas["EnvironmentRecord"];
export type CreateEnvironmentBody = Schemas["CreateEnvironmentBody"];
export type UpdateEnvironmentBody = Schemas["UpdateEnvironmentBody"];
export type EnvironmentsList = Schemas["EnvironmentsList"];
export type HealthResponse = Schemas["HealthResponse"];
export type AuditEntry = Schemas["AuditEntry"];
export type AuditActor = Schemas["AuditActor"];
export type AuditHead = Schemas["AuditHead"];
export type AuditListResponse = Schemas["AuditListResponse"];
export type AuditVerifyResponse = Schemas["AuditVerifyResponse"];
export type PendingInviteRecord = Schemas["PendingInviteRecord"];
export type PendingInvitesList = Schemas["PendingInvitesList"];
export type InviteAddedResponse = Schemas["InviteAddedResponse"];
export type InvitePendingResponse = Schemas["InvitePendingResponse"];
export type AcceptInviteResponse = Schemas["AcceptInviteResponse"];

export type ListAuditOptions = {
  since?: number;
  until?: number;
  actor?: string;
  action?: string;
  resource?: string;
  limit?: number;
  cursor?: number;
};

type RawClient = OpenapiClient<paths>;

type FetchLike = typeof fetch;

function buildHeaders(opts: WardkeyClientOptions): Record<string, string> {
  const headers: Record<string, string> = { accept: "application/json" };
  if (opts.apiToken) {
    Object.assign(headers, { authorization: `Bearer ${opts.apiToken}` });
  }
  if (opts.headers) Object.assign(headers, opts.headers);
  return headers;
}

/**
 * Wraps the raw fetch so we can intercept the response, surface request-id,
 * and translate non-2xx into WardkeyAPIError. openapi-fetch otherwise returns
 * `{ data, error }` and a missing-content 204 makes the type discriminant
 * awkward at the call site.
 */
function wrapFetch(opts: WardkeyClientOptions): FetchLike {
  const inner = opts.fetch ?? globalThis.fetch.bind(globalThis);
  const credentials: RequestCredentials | undefined = opts.useCookies ? "include" : undefined;
  return async (input, init = {}) => {
    const merged: RequestInit = { ...init };
    if (credentials) merged.credentials = credentials;
    return inner(input, merged);
  };
}

/**
 * Calls an openapi-fetch method and throws WardkeyAPIError on non-2xx. The
 * generated types make `data` and `error` exclusive — we collapse that to a
 * plain `data` return so the resource facades stay readable.
 */
async function unwrap<T>(
  call: Promise<{ data?: T; error?: unknown; response: Response }>,
): Promise<T> {
  const { data, error, response } = await call;
  if (response.ok) {
    // 204 / empty body — generated types say `never`. Cast to T.
    return (data as T) ?? (undefined as T);
  }
  throw toAPIError(error ?? null, response.status, response.headers.get(REQUEST_ID_HEADER));
}

class HealthAPI {
  constructor(private readonly raw: RawClient) {}
  check(): Promise<HealthResponse> {
    return unwrap(this.raw.GET("/v1/health", {}));
  }
}

class MeAPI {
  constructor(private readonly raw: RawClient) {}
  get(): Promise<Identity> {
    return unwrap(this.raw.GET("/v1/me", {}));
  }
  update(body: UpdateMeBody): Promise<UserRecord> {
    return unwrap(this.raw.PATCH("/v1/me", { body }));
  }
}

class OrgsAPI {
  constructor(private readonly raw: RawClient) {}
  create(body: CreateOrgBody): Promise<OrgRecord> {
    return unwrap(this.raw.POST("/v1/orgs", { body }));
  }
  get(orgId: string): Promise<OrgRecord> {
    return unwrap(this.raw.GET("/v1/orgs/{org_id}", { params: { path: { org_id: orgId } } }));
  }
  /** Update mutable org fields. Owner/admin only — the server enforces this. */
  update(orgId: string, body: UpdateOrgBody): Promise<OrgRecord> {
    return unwrap(
      this.raw.PATCH("/v1/orgs/{org_id}", { params: { path: { org_id: orgId } }, body }),
    );
  }
  /**
   * Hard-delete an org. The server refuses unless the caller is the owner AND
   * no other members remain — deleting an org with active teammates would
   * silently revoke their access and the audit chain would be unrecoverable.
   */
  delete(orgId: string): Promise<void> {
    return unwrap(this.raw.DELETE("/v1/orgs/{org_id}", { params: { path: { org_id: orgId } } }));
  }
}

class MembersAPI {
  constructor(private readonly raw: RawClient) {}
  list(orgId: string): Promise<MembersList> {
    return unwrap(
      this.raw.GET("/v1/orgs/{org_id}/members", { params: { path: { org_id: orgId } } }),
    );
  }
  invite(orgId: string, body: InviteMemberBody): Promise<InviteMemberResponse> {
    return unwrap(
      this.raw.POST("/v1/orgs/{org_id}/members", {
        params: { path: { org_id: orgId } },
        body,
      }),
    );
  }
  changeRole(orgId: string, userId: string, body: ChangeRoleBody): Promise<MembershipRecord> {
    return unwrap(
      this.raw.PATCH("/v1/orgs/{org_id}/members/{user_id}", {
        params: { path: { org_id: orgId, user_id: userId } },
        body,
      }),
    );
  }
  remove(orgId: string, userId: string): Promise<void> {
    return unwrap(
      this.raw.DELETE("/v1/orgs/{org_id}/members/{user_id}", {
        params: { path: { org_id: orgId, user_id: userId } },
      }),
    );
  }
}

class AuditAPI {
  constructor(private readonly raw: RawClient) {}
  list(orgId: string, opts: ListAuditOptions = {}): Promise<AuditListResponse> {
    const query: Record<string, string | number> = {};
    if (opts.since !== undefined) Object.assign(query, { since: opts.since });
    if (opts.until !== undefined) Object.assign(query, { until: opts.until });
    if (opts.actor !== undefined) Object.assign(query, { actor: opts.actor });
    if (opts.action !== undefined) Object.assign(query, { action: opts.action });
    if (opts.resource !== undefined) Object.assign(query, { resource: opts.resource });
    if (opts.limit !== undefined) Object.assign(query, { limit: opts.limit });
    if (opts.cursor !== undefined) Object.assign(query, { cursor: opts.cursor });
    return unwrap(
      this.raw.GET("/v1/orgs/{org_id}/audit", {
        params: { path: { org_id: orgId }, query },
      }),
    );
  }
  head(orgId: string): Promise<AuditHead> {
    return unwrap(
      this.raw.GET("/v1/orgs/{org_id}/audit/head", {
        params: { path: { org_id: orgId } },
      }),
    );
  }
  verify(orgId: string): Promise<AuditVerifyResponse> {
    return unwrap(
      this.raw.GET("/v1/orgs/{org_id}/audit/verify", {
        params: { path: { org_id: orgId } },
      }),
    );
  }
}

class SecretsAPI {
  constructor(private readonly raw: RawClient) {}
  list(orgId: string, project: string, env: string): Promise<SecretsList> {
    return unwrap(
      this.raw.GET("/v1/orgs/{org_id}/projects/{project}/envs/{env}/secrets", {
        params: { path: { org_id: orgId, project, env } },
      }),
    );
  }
  create(orgId: string, project: string, env: string, body: CreateSecretBody): Promise<SecretMeta> {
    return unwrap(
      this.raw.POST("/v1/orgs/{org_id}/projects/{project}/envs/{env}/secrets", {
        params: { path: { org_id: orgId, project, env } },
        body,
      }),
    );
  }
  update(
    orgId: string,
    project: string,
    env: string,
    secretPath: string,
    body: UpdateSecretBody,
  ): Promise<SecretVersionResponse> {
    return unwrap(
      this.raw.PATCH("/v1/orgs/{org_id}/projects/{project}/envs/{env}/secrets/{path}", {
        params: { path: { org_id: orgId, project, env, path: secretPath } },
        body,
      }),
    );
  }
  remove(orgId: string, project: string, env: string, secretPath: string): Promise<void> {
    return unwrap(
      this.raw.DELETE("/v1/orgs/{org_id}/projects/{project}/envs/{env}/secrets/{path}", {
        params: { path: { org_id: orgId, project, env, path: secretPath } },
      }),
    );
  }
  readValue(orgId: string, project: string, env: string, secretPath: string): Promise<SecretValue> {
    return unwrap(
      this.raw.GET("/v1/orgs/{org_id}/projects/{project}/envs/{env}/secrets/{path}/value", {
        params: { path: { org_id: orgId, project, env, path: secretPath } },
      }),
    );
  }
  readVersion(
    orgId: string,
    project: string,
    env: string,
    secretPath: string,
    version: number,
  ): Promise<SecretValue> {
    return unwrap(
      this.raw.GET(
        "/v1/orgs/{org_id}/projects/{project}/envs/{env}/secrets/{path}/versions/{version}/value",
        {
          params: { path: { org_id: orgId, project, env, path: secretPath, version } },
        },
      ),
    );
  }
  listVersions(
    orgId: string,
    project: string,
    env: string,
    secretPath: string,
  ): Promise<VersionsList> {
    return unwrap(
      this.raw.GET("/v1/orgs/{org_id}/projects/{project}/envs/{env}/secrets/{path}/versions", {
        params: { path: { org_id: orgId, project, env, path: secretPath } },
      }),
    );
  }
  /**
   * Change a secret's visibility tier. Downgrades (sealed→hidden, sealed→open,
   * hidden→open) MUST include `value` + `content_type` so the relaxation
   * rotates atomically. Upgrades may omit those fields.
   */
  setVisibility(
    orgId: string,
    project: string,
    env: string,
    secretPath: string,
    body: SetSecretVisibilityBody,
  ): Promise<SecretMeta> {
    return unwrap(
      this.raw.PATCH(
        "/v1/orgs/{org_id}/projects/{project}/envs/{env}/secrets/{path}/visibility",
        {
          params: { path: { org_id: orgId, project, env, path: secretPath } },
          body,
        },
      ),
    );
  }
}

class ServiceTokensAPI {
  constructor(private readonly raw: RawClient) {}
  list(orgId: string): Promise<ServiceTokensList> {
    return unwrap(
      this.raw.GET("/v1/orgs/{org_id}/service-tokens", {
        params: { path: { org_id: orgId } },
      }),
    );
  }
  /**
   * Mint a new service token. The returned plaintext is shown exactly once;
   * Wardkey discards it after the response. Owner/admin only.
   */
  mint(orgId: string, body: MintServiceTokenBody): Promise<MintServiceTokenResponse> {
    return unwrap(
      this.raw.POST("/v1/orgs/{org_id}/service-tokens", {
        params: { path: { org_id: orgId } },
        body,
      }),
    );
  }
  /** Revoke a token. Idempotent — already-revoked tokens still return 204. */
  revoke(orgId: string, tokenId: string): Promise<void> {
    return unwrap(
      this.raw.DELETE("/v1/orgs/{org_id}/service-tokens/{token_id}", {
        params: { path: { org_id: orgId, token_id: tokenId } },
      }),
    );
  }
}

class ProjectsAPI {
  constructor(private readonly raw: RawClient) {}
  list(orgId: string): Promise<ProjectsList> {
    return unwrap(
      this.raw.GET("/v1/orgs/{org_id}/projects", { params: { path: { org_id: orgId } } }),
    );
  }
  create(orgId: string, body: CreateProjectBody): Promise<ProjectRecord> {
    return unwrap(
      this.raw.POST("/v1/orgs/{org_id}/projects", {
        params: { path: { org_id: orgId } },
        body,
      }),
    );
  }
  get(orgId: string, projectSlug: string): Promise<ProjectRecord> {
    return unwrap(
      this.raw.GET("/v1/orgs/{org_id}/projects/{project_slug}", {
        params: { path: { org_id: orgId, project_slug: projectSlug } },
      }),
    );
  }
  update(orgId: string, projectSlug: string, body: UpdateProjectBody): Promise<ProjectRecord> {
    return unwrap(
      this.raw.PATCH("/v1/orgs/{org_id}/projects/{project_slug}", {
        params: { path: { org_id: orgId, project_slug: projectSlug } },
        body,
      }),
    );
  }
  remove(orgId: string, projectSlug: string): Promise<void> {
    return unwrap(
      this.raw.DELETE("/v1/orgs/{org_id}/projects/{project_slug}", {
        params: { path: { org_id: orgId, project_slug: projectSlug } },
      }),
    );
  }
}

class EnvironmentsAPI {
  constructor(private readonly raw: RawClient) {}
  list(orgId: string, projectSlug: string): Promise<EnvironmentsList> {
    return unwrap(
      this.raw.GET("/v1/orgs/{org_id}/projects/{project_slug}/envs", {
        params: { path: { org_id: orgId, project_slug: projectSlug } },
      }),
    );
  }
  create(
    orgId: string,
    projectSlug: string,
    body: CreateEnvironmentBody,
  ): Promise<EnvironmentRecord> {
    return unwrap(
      this.raw.POST("/v1/orgs/{org_id}/projects/{project_slug}/envs", {
        params: { path: { org_id: orgId, project_slug: projectSlug } },
        body,
      }),
    );
  }
  get(orgId: string, projectSlug: string, envSlug: string): Promise<EnvironmentRecord> {
    return unwrap(
      this.raw.GET("/v1/orgs/{org_id}/projects/{project_slug}/envs/{env_slug}", {
        params: { path: { org_id: orgId, project_slug: projectSlug, env_slug: envSlug } },
      }),
    );
  }
  update(
    orgId: string,
    projectSlug: string,
    envSlug: string,
    body: UpdateEnvironmentBody,
  ): Promise<EnvironmentRecord> {
    return unwrap(
      this.raw.PATCH("/v1/orgs/{org_id}/projects/{project_slug}/envs/{env_slug}", {
        params: { path: { org_id: orgId, project_slug: projectSlug, env_slug: envSlug } },
        body,
      }),
    );
  }
  remove(orgId: string, projectSlug: string, envSlug: string): Promise<void> {
    return unwrap(
      this.raw.DELETE("/v1/orgs/{org_id}/projects/{project_slug}/envs/{env_slug}", {
        params: { path: { org_id: orgId, project_slug: projectSlug, env_slug: envSlug } },
      }),
    );
  }
}

/**
 * Pending-invite operations (WARD-108). Distinct from `MembersAPI.invite`
 * because the invite-create call lives on the members endpoint (it may
 * resolve to either an immediate add or a new pending invite, hence the
 * `InviteMemberResponse` discriminated union there). This facade covers the
 * list / revoke / resend / accept side that operates on existing pending
 * rows.
 */
class InvitesAPI {
  constructor(private readonly raw: RawClient) {}
  /** Owner/admin only. Returns invites that haven't been accepted or expired. */
  list(orgId: string): Promise<PendingInvitesList> {
    return unwrap(
      this.raw.GET("/v1/orgs/{org_id}/invites", { params: { path: { org_id: orgId } } }),
    );
  }
  /** Owner/admin only. Idempotent — returns 204 even if the invite is already gone. */
  revoke(orgId: string, inviteId: string): Promise<void> {
    return unwrap(
      this.raw.DELETE("/v1/orgs/{org_id}/invites/{invite_id}", {
        params: { path: { org_id: orgId, invite_id: inviteId } },
      }),
    );
  }
  /**
   * Owner/admin only. Rotates the token on the invite and re-sends the email.
   * Returns the updated `PendingInviteRecord` (with the new `expires_at`).
   * The previous email link is invalidated.
   */
  resend(orgId: string, inviteId: string): Promise<PendingInviteRecord> {
    return unwrap(
      this.raw.POST("/v1/orgs/{org_id}/invites/{invite_id}/resend", {
        params: { path: { org_id: orgId, invite_id: inviteId } },
      }),
    );
  }
  /**
   * Consume an invite from the email link. Requires a signed-in session — the
   * caller's email must match the invite's email (case-insensitive). On
   * success the new membership is created and the invite row deleted.
   */
  accept(token: string): Promise<AcceptInviteResponse> {
    return unwrap(this.raw.POST("/v1/invites/{token}/accept", { params: { path: { token } } }));
  }
}

export class WardkeyClient {
  readonly baseUrl: string;
  readonly health: HealthAPI;
  readonly me: MeAPI;
  readonly orgs: OrgsAPI;
  readonly members: MembersAPI;
  readonly invites: InvitesAPI;
  readonly projects: ProjectsAPI;
  readonly envs: EnvironmentsAPI;
  readonly secrets: SecretsAPI;
  readonly serviceTokens: ServiceTokensAPI;
  readonly audit: AuditAPI;
  /** Underlying openapi-fetch client — exposed for advanced cases the facade does not cover. */
  readonly raw: RawClient;

  constructor(options: WardkeyClientOptions = {}) {
    if (options.apiToken && options.useCookies) {
      throw new Error("WardkeyClient: pass either `apiToken` or `useCookies`, not both.");
    }
    this.baseUrl = (options.baseUrl ?? DEFAULT_API_BASE_URL).replace(/\/+$/, "");
    this.raw = createOpenapiClient<paths>({
      baseUrl: this.baseUrl,
      headers: buildHeaders(options),
      fetch: wrapFetch(options),
    });
    this.health = new HealthAPI(this.raw);
    this.me = new MeAPI(this.raw);
    this.orgs = new OrgsAPI(this.raw);
    this.members = new MembersAPI(this.raw);
    this.invites = new InvitesAPI(this.raw);
    this.projects = new ProjectsAPI(this.raw);
    this.envs = new EnvironmentsAPI(this.raw);
    this.secrets = new SecretsAPI(this.raw);
    this.serviceTokens = new ServiceTokensAPI(this.raw);
    this.audit = new AuditAPI(this.raw);
  }
}

export { WardkeyAPIError };
